// ===========================================
// COMPONENTE: EnviarPresentadoEmailDialog
// ===========================================
// Dialog post-presentación para enviar email de notificación al cliente
// con asunto y cuerpo editables + adjuntos de la presentación.

import { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Loader2, Mail, Paperclip, RefreshCw, SkipForward } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth0 } from '@/lib/auth'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useConfiguracionEstudio } from '@/hooks/useConfiguracion'
import {
  ConfiguracionEstudioNoDisponibleError,
  exigirConfiguracionEstudio,
} from '@/services/configuracionService'
import { emailService } from '@/services/emailService'
import { formatCurrency } from '@/emails/helpers'
import { formatPeriodoLargo } from '@/lib/formatPeriodo'
import { generarMensajeMonotributo } from './mensajeMonotributo'
import { primerNombreCliente } from './presentacionNaming'

// ===========================================
// TYPES
// ===========================================

export interface DatosPresentadoEmail {
  vencimiento: {
    id: string
    cliente_id: string | null   // ORR-198 A6: para que email_log.cliente_id no quede NULL
    tipo_impuesto: string
    cliente_nombre: string
    cliente_email: string
    periodo: string
    fecha_vencimiento: string
  }
  presentacion: {
    /** Presentation day as YYYY-MM-DD. */
    fecha_presentacion: string
    numero_presentacion?: string
    monto_determinado?: number
    saldoTipo?: 'a_pagar' | 'a_favor' | 'sin_movimiento'
    forma_pago?: 'VEP' | 'debito_automatico' | 'efectivo'
    observaciones?: string
    mensaje_libre?: string
  }
  adjuntosSubidos: Array<{ docId: string; nombre: string; path: string }>
}

interface EnviarPresentadoEmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  datos: DatosPresentadoEmail | null
  /** Names of the uploaded attachments whose link to the vencimiento failed. */
  adjuntosSinVincular?: string[]
  /** Links again the attachments whose link failed. */
  onReintentarVinculos?: () => Promise<void>
}

// ===========================================
// HELPERS
// ===========================================

function generarMensajeDefault(datos: DatosPresentadoEmail): string {
  const { vencimiento, presentacion } = datos
  // ORR-177: el cierre ya no lleva el nombre del usuario (Lisandro lo pidió sacar).
  // El pie/firma configurable del estudio va al final como banner (firma_email_html).
  const cierre = 'Saludos,'

  // For manual vencimientos, use the user-provided message
  if (presentacion.mensaje_libre) {
    return `${presentacion.mensaje_libre}

${cierre}`
  }

  // Monotributo con forma de pago: mensaje específico al cliente (3 templates)
  if (vencimiento.tipo_impuesto === 'Monotributo' && presentacion.forma_pago) {
    const monto = presentacion.monto_determinado != null
      ? formatCurrency(presentacion.monto_determinado)
      : ''
    const fechaVenc = vencimiento.fecha_vencimiento
      ? new Date(vencimiento.fecha_vencimiento + 'T00:00:00').toLocaleDateString('es-AR')
      : ''
    return `${generarMensajeMonotributo(presentacion.forma_pago, {
      monto,
      fechaVencimiento: fechaVenc,
    })}

${cierre}`
  }

  // Fiscal vencimientos: auto-generated message
  const fechaPresentacion = new Date(presentacion.fecha_presentacion + 'T00:00:00').toLocaleDateString('es-AR')
  const periodoLargo = formatPeriodoLargo(vencimiento.periodo) || vencimiento.periodo
  const fechaVencLinea = vencimiento.fecha_vencimiento
    ? `, la cual tenía como fecha de vencimiento el día ${new Date(vencimiento.fecha_vencimiento + 'T00:00:00').toLocaleDateString('es-AR')}`
    : ''

  let montoLinea = ''
  if (presentacion.saldoTipo === 'sin_movimiento') {
    montoLinea = '\n\nLa declaración se presentó sin movimiento.'
  } else if (presentacion.saldoTipo === 'a_favor' && presentacion.monto_determinado != null) {
    montoLinea = `\n\nEl saldo determinado es a favor por ${formatCurrency(Math.abs(presentacion.monto_determinado))}.`
  } else if (presentacion.monto_determinado != null && presentacion.monto_determinado > 0) {
    montoLinea = `\n\nEl monto determinado a pagar es de ${formatCurrency(presentacion.monto_determinado)}.`
  }

  const adjuntosLinea = datos.adjuntosSubidos.length > 0
    ? '\n\nAdjuntamos la documentación correspondiente para su archivo.'
    : ''

  return `Le informamos que con fecha ${fechaPresentacion} hemos realizado la presentación de ${vencimiento.tipo_impuesto} correspondiente al período ${periodoLargo}${fechaVencLinea}.${montoLinea}${adjuntosLinea}

${cierre}`
}

// ===========================================
// COMPONENT
// ===========================================

export function EnviarPresentadoEmailDialog({
  open,
  onOpenChange,
  datos,
  adjuntosSinVincular = [],
  onReintentarVinculos,
}: EnviarPresentadoEmailDialogProps) {
  const { getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const { data: configEstudio, isError: configEstudioError } = useConfiguracionEstudio()

  const [asunto, setAsunto] = useState('')
  const [cuerpo, setCuerpo] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [reintentando, setReintentando] = useState(false)
  const nombresSinVincular = adjuntosSinVincular.join(', ')

  const handleReintentarVinculos = async () => {
    if (!onReintentarVinculos) return
    setReintentando(true)
    try {
      await onReintentarVinculos()
    } finally {
      setReintentando(false)
    }
  }

  // The recipient is always the email in the client's record. The server rejects any other address.
  const emailDestino = datos?.vencimiento.cliente_email ?? ''
  const clienteSinEmail = !emailDestino.trim()
  const emailValido = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailDestino),
    [emailDestino],
  )

  // Pre-fill when dialog opens with new data
  useEffect(() => {
    if (open && datos) {
      const nombreEstudio = configEstudio?.nombre || 'El estudio'
      setAsunto(`Presentación ${datos.vencimiento.tipo_impuesto} ${datos.vencimiento.periodo} - ${nombreEstudio}`)
      setCuerpo(generarMensajeDefault(datos))
    }
  }, [open, datos, configEstudio?.nombre])

  const handleEnviar = async () => {
    if (!datos || !emailValido) return
    if (!organizacionId) {
      toast.error('No se pudo identificar la organización. Recargá la página.')
      return
    }

    setEnviando(true)
    try {
      const estudio = exigirConfiguracionEstudio({
        data: configEstudio,
        isError: configEstudioError,
      })
      const token = await getAccessTokenSilently()

      const result = await emailService.enviarPresentadoCliente(
        token,
        emailDestino,
        {
          clienteNombre: primerNombreCliente(datos.vencimiento.cliente_nombre),
          tipoImpuesto: datos.vencimiento.tipo_impuesto,
          periodo: datos.vencimiento.periodo,
          fechaPresentacion: datos.presentacion.fecha_presentacion,
          numeroPresentacion: datos.presentacion.numero_presentacion ?? null,
          montoDeterminado: datos.presentacion.monto_determinado ?? null,
          saldoTipo: datos.presentacion.saldoTipo ?? null,
          adjuntoNombres: datos.adjuntosSubidos.map((a) => a.nombre),
          mensajePersonalizado: cuerpo,
          customSubject: asunto,
          whatsappEstudio: estudio?.telefono ?? null,
          nombreEstudio: estudio?.nombre ?? null,
        },
        {
          organizacionId,
          clienteId: datos.vencimiento.cliente_id ?? undefined,
          documentoIds: datos.adjuntosSubidos.map((a) => a.docId),
        }
      )

      if (result.success) {
        toast.success(`Notificación enviada a ${emailDestino}`)
        onOpenChange(false)
      } else {
        toast.error(result.error || 'Error al enviar la notificación')
      }
    } catch (error) {
      console.error('Error enviando email de presentación:', error)
      toast.error(
        error instanceof ConfiguracionEstudioNoDisponibleError
          ? error.message
          : 'Error al enviar la notificación',
      )
    } finally {
      setEnviando(false)
    }
  }

  if (!datos) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-emerald-600" />
            Enviar notificación al cliente
          </DialogTitle>
          <DialogDescription>
            Notificar a {datos.vencimiento.cliente_nombre} sobre la presentación realizada
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Destinatario */}
          <div className="space-y-2">
            <Label htmlFor="email-destinatario">Destinatario</Label>
            {clienteSinEmail ? (
              <p className="text-sm text-amber-600">
                El cliente no tiene email en la ficha. Cargalo en la ficha del cliente y reenviá la notificación desde el
                calendario, en «Enviar notificación».
              </p>
            ) : (
              <Input id="email-destinatario" type="email" value={emailDestino} disabled className="bg-muted" />
            )}
          </div>

          {/* Asunto (editable) */}
          <div className="space-y-2">
            <Label htmlFor="asunto-email">Asunto</Label>
            <Input
              id="asunto-email"
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
              disabled={enviando}
            />
          </div>

          {/* Cuerpo (editable) */}
          <div className="space-y-2">
            <Label htmlFor="cuerpo-email">Mensaje</Label>
            <Textarea
              id="cuerpo-email"
              value={cuerpo}
              onChange={(e) => setCuerpo(e.target.value)}
              rows={6}
              className="resize-none"
              disabled={enviando}
            />
            <p className="text-xs text-muted-foreground">
              El email incluirá automáticamente los detalles de la presentación en formato visual.
            </p>
          </div>

          {/* Adjuntos (read-only) */}
          {datos.adjuntosSubidos.length > 0 && (
            <div className="space-y-2">
              <Label>Adjuntos</Label>
              <div className="space-y-1">
                {datos.adjuntosSubidos.map((adjunto, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Paperclip className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{adjunto.nombre}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attachments whose link to the vencimiento failed */}
          {adjuntosSinVincular.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm text-amber-600">
                {adjuntosSinVincular.length === 1
                  ? `Se marcó como presentado, pero no quedó vinculado al vencimiento: ${nombresSinVincular}. Este envío lo incluye; un reenvío posterior no lo va a encontrar.`
                  : `Se marcó como presentado, pero no quedaron vinculados al vencimiento: ${nombresSinVincular}. Este envío los incluye; un reenvío posterior no los va a encontrar.`}
              </p>
              {onReintentarVinculos && (
                <Button type="button" variant="outline" size="sm" onClick={handleReintentarVinculos} disabled={reintentando}>
                  {reintentando ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Reintentar vínculo
                </Button>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={enviando}
          >
            <SkipForward className="h-4 w-4 mr-2" />
            Omitir
          </Button>
          <Button
            onClick={handleEnviar}
            disabled={enviando || !asunto.trim() || !cuerpo.trim() || !emailValido}
          >
            {enviando ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-2 h-4 w-4" />
            )}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
